export interface TrajectoryPoint {
  timeMs: number;
  x: number;
  y: number;
}

/** Posição da trajetória no instante `elapsedMs`, interpolando entre os pontos de 40 ms que o servidor enviou. */
export function positionAt(points: readonly TrajectoryPoint[], elapsedMs: number): { x: number; y: number; done: boolean; angle: number } {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return { x: 0, y: 0, done: true, angle: 0 };
  if (elapsedMs <= first.timeMs) return { x: first.x, y: first.y, done: false, angle: headingBetween(first, points[1] ?? first) };
  if (elapsedMs >= last.timeMs) return { x: last.x, y: last.y, done: true, angle: headingBetween(points[points.length - 2] ?? last, last) };

  // Busca binária: a trajetória pode ter mais de mil pontos.
  let lo = 0;
  let hi = points.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if ((points[mid]?.timeMs ?? 0) <= elapsedMs) lo = mid;
    else hi = mid;
  }
  const a = points[lo]!;
  const b = points[hi]!;
  const span = b.timeMs - a.timeMs;
  const t = span <= 0 ? 0 : (elapsedMs - a.timeMs) / span;
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, done: false, angle: headingBetween(a, b) };
}

function headingBetween(a: TrajectoryPoint, b: TrajectoryPoint): number {
  return Math.atan2(b.y - a.y, b.x - a.x);
}

/**
 * Relógio do servidor visto do cliente. Cada medição (pong: instante local de envio, instante do servidor, instante local de chegada)
 * dá um deslocamento; guarda-se a média móvel das últimas para a hora do turno não tremer.
 */
export class ClockSync {
  private offsets: number[] = [];

  constructor(
    private readonly localNow: () => number = Date.now,
    private readonly keep = 8,
  ) {}

  /** `clientSentMs` e o instante local da chegada medem o RTT; o servidor carimbou no meio do caminho. */
  observe(clientSentMs: number, serverMs: number, arrivedMs = this.localNow()): void {
    const rtt = Math.max(0, arrivedMs - clientSentMs);
    this.offsets.push(serverMs + rtt / 2 - arrivedMs);
    if (this.offsets.length > this.keep) this.offsets.shift();
  }

  /** Para mensagens que carregam o relógio do servidor (snapshot): sem RTT, assume latência zero. */
  observeServerTime(serverMs: number): void {
    this.observe(this.localNow(), serverMs, this.localNow());
  }

  get offsetMs(): number {
    if (this.offsets.length === 0) return 0;
    return this.offsets.reduce((sum, v) => sum + v, 0) / this.offsets.length;
  }

  serverNow(): number {
    return this.localNow() + this.offsetMs;
  }

  /** Milissegundos que faltam para `serverTimeMs` (negativo se já passou). */
  until(serverTimeMs: number): number {
    return serverTimeMs - this.serverNow();
  }
}
